/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { BaseFeatureMock } from '../mockServer/featureMock';
import { StoreType } from '../stores/storesClientTypings';
import {
    ActivityDto,
    CashRegisterModelShortDto,
    CityResource,
    FirmwareVersionDTO,
    FiscalDriverVersionDto,
    OperatorShortDto,
    RegionResource,
    StoreLowerActivity,
    StoreMainActivity,
    StoreMiddleActivity,
    StoreTypeResource,
    TaxationTypeDTO,
    TaxAuthorityDTO,
    UserShortDto,
} from './shortListsClient.typings';
import {
    cashRegisterModelShortDtoFactory,
    cityDtoFactory,
    firmwareVersionFactory,
    fiscalDriverVersionFactory,
    lowerActivitiesDtoFactory,
    mainActivitiesDtoFactory,
    middleActivitiesDtoFactory,
    operatorShortDtoFactory,
    regionDtoFactory,
    storeTypeFactory,
    taxationTypeFactory,
    taxAuthorityFactory,
    userShortDtoFactory,
} from './shortListsFactories';
import { TAXATION_TYPES } from '../companies/companiesClient.typings';

const CASH_REGISTERS_MODELS_URL: RegExp = /\/cashRegisterModels\/short$/;
const OPERATORS_URL: RegExp = /\/operators\/short$/;
const USERS_URL: RegExp = /\/users\/short$/;
const STORE_TYPES_URL: RegExp = /\/stores\/types$/;
const FIRMWARE_VERSIONS_URL: RegExp = /\/firmwareVersions$/;
const FISCAL_DRIVER_VERSIONS_URL: RegExp = /\/fiscalDriverVersions$/;
const TAX_AUTHORITIES_URL: RegExp = /\/taxAuthorities$/;
const TAXATION_TYPES_URL: RegExp = /\/taxationTypes$/;
const CITIES_URL: RegExp = /\/cities$/;
const REGIONS_URL: RegExp = /\/regions$/;
const MAIN_ACTIVITIES_LIST_URL: RegExp = /\/activities\/main$/;
const MIDDLE_ACTIVITIES_LIST_URL: RegExp = /\/activities\/middle$/;
const LOWER_ACTIVITIES_LIST_URL: RegExp = /\/activities\/lower$/;

class ShortListsFeatureMock extends BaseFeatureMock {
    public readonly cashRegisterModels = this.route<CashRegisterModelShortDto[]>({
        method: 'get',
        url: CASH_REGISTERS_MODELS_URL,
        defaultResponse: () => {
            const data: CashRegisterModelShortDto[] = cashRegisterModelShortDtoFactory.list({ count: 100 });
            cashRegisterModelShortDtoFactory.resetCount();
            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly operatorsShort = this.route<OperatorShortDto[]>({
        method: 'get',
        url: OPERATORS_URL,
        defaultResponse: () => {
            const data: OperatorShortDto[] = operatorShortDtoFactory.list({ count: 22 });
            operatorShortDtoFactory.resetCount();

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly usersShort = this.route<UserShortDto[]>({
        method: 'get',
        url: USERS_URL,
        defaultResponse: () => {
            const data: UserShortDto[] = userShortDtoFactory.list({ count: 30 });
            userShortDtoFactory.resetCount();

            return [200, data];
        },
        delayMs: () => Math.random() * 200,
    });

    public readonly storeTypes = this.route<StoreTypeResource[]>({
        method: 'get',
        url: STORE_TYPES_URL,
        defaultResponse: () => {
            const data: StoreTypeResource[] = storeTypeFactory.list({ count: Object.values(StoreType).length });
            storeTypeFactory.resetCount();

            return [200, data];
        },
        delayMs: () => Math.random() * 200,
    });

    public readonly firmwareVersions = this.route<FirmwareVersionDTO[]>({
        method: 'get',
        url: FIRMWARE_VERSIONS_URL,
        defaultResponse: () => {
            const data: FirmwareVersionDTO[] = firmwareVersionFactory.list({ count: 10 });
            firmwareVersionFactory.resetCount();

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly fiscalDriverVersions = this.route<FiscalDriverVersionDto[]>({
        method: 'get',
        url: FISCAL_DRIVER_VERSIONS_URL,
        defaultResponse: () => {
            const data: FiscalDriverVersionDto[] = fiscalDriverVersionFactory.list({ count: 100 });
            // Pre-existing quirk, preserved intentionally for behavior parity: this resets
            // `firmwareVersionFactory` instead of `fiscalDriverVersionFactory` (copy/paste bug).
            firmwareVersionFactory.resetCount();

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly taxAuthorities = this.route<TaxAuthorityDTO[]>({
        method: 'get',
        url: TAX_AUTHORITIES_URL,
        defaultResponse: () => {
            const data: TaxAuthorityDTO[] = taxAuthorityFactory.list({ count: 50 });
            taxAuthorityFactory.resetCount();

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly taxationTypes = this.route<TaxationTypeDTO[]>({
        method: 'get',
        url: TAXATION_TYPES_URL,
        defaultResponse: () => {
            const data: TaxationTypeDTO[] = taxationTypeFactory.list({ count: TAXATION_TYPES.length });
            taxationTypeFactory.resetCount();

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly cities = this.route<CityResource[]>({
        method: 'get',
        url: CITIES_URL,
        defaultResponse: () => {
            const data = cityDtoFactory.list({ count: 1000 });

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly regions = this.route<RegionResource[]>({
        method: 'get',
        url: REGIONS_URL,
        defaultResponse: () => {
            const data = regionDtoFactory.list({ count: 100 });

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly mainActivities = this.route<ActivityDto[]>({
        method: 'get',
        url: MAIN_ACTIVITIES_LIST_URL,
        defaultResponse: () => {
            const data = mainActivitiesDtoFactory.list({ count: Object.values(StoreMainActivity).length + 4 });

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly middleActivities = this.route<ActivityDto[]>({
        method: 'get',
        url: MIDDLE_ACTIVITIES_LIST_URL,
        defaultResponse: () => {
            const data = middleActivitiesDtoFactory.list({ count: Object.values(StoreMiddleActivity).length + 4 });

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly lowerActivities = this.route<ActivityDto[]>({
        method: 'get',
        url: LOWER_ACTIVITIES_LIST_URL,
        defaultResponse: () => {
            const data = lowerActivitiesDtoFactory.list({ count: Object.values(StoreLowerActivity).length + 4 });

            return [200, data];
        },
        delayMs: 300,
    });

    protected onReset(): void {
        cityDtoFactory.resetCount();
        regionDtoFactory.resetCount();
        mainActivitiesDtoFactory.resetCount();
        middleActivitiesDtoFactory.resetCount();
        lowerActivitiesDtoFactory.resetCount();
    }
}

export const shortListsMock = new ShortListsFeatureMock();

/** Backward-compat shim - dev mock mode (mockAdapter.ts) keeps calling this untouched. */
export function addShortListsMockApi(mockAdapter: MockAdapter): void {
    shortListsMock.register(mockAdapter, { delay: true });
}
